import { useState } from 'react';
import type { SeriesCreateRequest } from '../../api/types';
import { Badge, Button, Select, TextInput } from '../../ui';

export type Fixture = {
  id: string;
  competition: string;
  pool: string;
  round: number;
  leg: number;
  team_a_id: string;
  team_b_id: string;
  team_a_name: string;
  team_b_name: string;
  settings: SeriesCreateRequest;
  status: string;
  error?: string;
  series_id?: string;
  scheduled_at?: string;
};

export const canQueue = (fixture: Fixture) =>
  fixture.status === 'draft' ||
  (fixture.status === 'failed' && !fixture.series_id);

export function FixtureTable({
  fixtures,
  pending,
  onAction,
  onQueue,
  onManageSeries,
}: {
  fixtures: Fixture[];
  pending: boolean;
  onAction: (fixture: Fixture, action: string) => void;
  onQueue: (fixtures: Fixture[]) => void;
  onManageSeries?: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const visible = fixtures.filter(
    (f) =>
      (!status || f.status === status) &&
      `${f.competition} ${f.pool} ${f.round} ${f.team_a_name} ${f.team_b_name}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const eligible = visible.filter(canQueue);
  // Batch actions always apply to visible, eligible rows. Hidden selections never execute.
  const picked = eligible.filter((f) => selected.includes(f.id));
  const allSelected = eligible.length > 0 && picked.length === eligible.length;

  return (
    <section className="mt-6" aria-label="Fixture schedule">
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line py-4">
        <div>
          <h3 className="text-base font-semibold">Match schedule</h3>
          <p className="mt-1 text-xs text-ink-dim">
            {visible.length} of {fixtures.length} fixtures · Queue starts when a
            bot and both teams are available.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <TextInput
            aria-label="Filter fixtures"
            placeholder="Find team, pool or round…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected([]);
            }}
          />
          <Select
            aria-label="Fixture status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setSelected([]);
            }}
          >
            <option value="">All statuses</option>
            {Array.from(new Set(fixtures.map((f) => f.status))).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Select>
        </div>
      </div>
      {picked.length > 0 && (
        <div
          className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-gold-400/40 bg-gold-400/10 p-3"
          aria-live="polite"
        >
          <span className="text-sm">{picked.length} fixtures selected</span>
          <Button
            size="sm"
            variant="primary"
            disabled={pending}
            onClick={() => {
              onQueue(picked);
              setSelected([]);
            }}
          >
            Queue selected
          </Button>
          <Button size="sm" onClick={() => setSelected([])}>
            Clear selection
          </Button>
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[680px] text-left text-sm">
          <caption className="sr-only">
            Tournament fixtures, schedules and queue actions
          </caption>
          <thead className="bg-surface-2 text-xs text-ink-dim">
            <tr>
              <th scope="col" className="w-10 p-3">
                <input
                  type="checkbox"
                  aria-label="Select all visible draft fixtures"
                  checked={allSelected}
                  disabled={!eligible.length || pending}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked ? eligible.map((f) => f.id) : [],
                    )
                  }
                />
              </th>
              {['Matchup', 'Round', 'Status', 'Scheduled', 'Actions'].map(
                (label) => (
                  <th scope="col" key={label} className="px-3 py-3 font-medium">
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {visible.map((f) => (
              <tr key={f.id} className="align-top hover:bg-surface-2/50">
                <td className="p-3">
                  {canQueue(f) && (
                    <input
                      type="checkbox"
                      aria-label={`Select ${f.team_a_name} vs ${f.team_b_name}`}
                      checked={selected.includes(f.id)}
                      disabled={pending}
                      onChange={(e) =>
                        setSelected((ids) =>
                          e.target.checked
                            ? [...ids, f.id]
                            : ids.filter((id) => id !== f.id),
                        )
                      }
                    />
                  )}
                </td>
                <td className="px-3 py-4">
                  <div className="font-semibold">
                    {f.team_a_name}{' '}
                    <span className="px-1 font-normal text-ink-faint">vs</span>{' '}
                    {f.team_b_name}
                  </div>
                  <p className="mt-1 text-xs text-ink-dim">
                    {f.competition} · {f.settings.format.toUpperCase()}
                  </p>
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  {f.pool}
                  <p className="mt-1 text-xs text-ink-dim">
                    Round {f.round} · Leg {f.leg}
                  </p>
                </td>
                <td className="max-w-64 px-3 py-4">
                  <Badge
                    tone={
                      f.error
                        ? 'warn'
                        : f.status === 'finished'
                          ? 'ok'
                          : 'neutral'
                    }
                  >
                    {f.status.replaceAll('_', ' ')}
                  </Badge>
                  {f.error && (
                    <p className="mt-2 text-xs text-warn">{f.error}</p>
                  )}
                </td>
                <td className="px-3 py-4 text-xs text-ink-dim">
                  {f.scheduled_at ? (
                    <time dateTime={f.scheduled_at}>
                      {new Date(f.scheduled_at).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </time>
                  ) : (
                    'When queued'
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    {canQueue(f) && (
                      <Button
                        size="sm"
                        disabled={pending}
                        onClick={() => onAction(f, 'queue')}
                      >
                        {f.status === 'failed' ? 'Retry queue' : 'Queue'}
                      </Button>
                    )}
                    {f.status === 'queued' && (
                      <>
                        <Button
                          size="sm"
                          disabled={pending}
                          onClick={() => onAction(f, 'first')}
                        >
                          Run first
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={pending}
                          onClick={() => onAction(f, 'draft')}
                        >
                          Unqueue
                        </Button>
                      </>
                    )}
                    {f.series_id && (
                      <Button
                        size="sm"
                        onClick={() => onManageSeries?.(f.series_id!)}
                      >
                        Open series →
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-medium">
              {fixtures.length
                ? 'No matching fixtures'
                : 'Your next round starts here'}
            </p>
            <p className="mt-2 text-sm text-ink-dim">
              {fixtures.length
                ? 'Adjust the search or status filter.'
                : 'Prepare fixtures above, review the starters, then queue your matches.'}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
